import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useAuth } from '../components/shared/AuthContext';
import {
  PageLayout,
  PageHeader,
  Spinner
} from '../components/ui';

const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { minimumFractionDigits: 2 })
    : "0.00";

// ─────────────────────────────────────────────────────────────────────────────
// Table styling.
//
// One brand-blue header band, matching every other table in the app. A two-tone
// amber/teal header was tried and dropped: it separated the two halves nicely
// but made this one screen look unlike the rest of the product, which matters
// more than the separation did.
//
// The centre divider now carries that job on its own — it is neutral grey, so it
// works under any header colour.
// ─────────────────────────────────────────────────────────────────────────────
// Every utility here carries `!`, and it has to.
//
// index.css:184 has a bare `th { background-color: … !important; color: …
// !important; padding: 16px 12px !important }` that applies to every table in
// the app. Specificity cannot beat !important, so a plain `bg-[#004BB5]` was
// generated, applied, and silently overridden — the header rendered in the
// global grey no matter what was set here.
//
// The padding is included for the same reason: without !px-4 !py-3 the global
// 16px/12px wins and the header no longer lines up with the body cells.
//
// Fixed here rather than in index.css because that rule styles every table in
// the product; unpicking it is its own task.
const thBase = "!px-4 !py-3 !bg-[#004BB5] !text-white";

// Emphasis on account-group names and totals, one colour per side.
const LIB_ACCENT = "text-[#C2410C]";   // liabilities & equity
const AST_ACCENT = "text-[#004BB5]";   // assets, brand blue

// Runs the full height of the table, on the first cell of the assets side. This
// is what makes the two halves read as two statements rather than one grid.
const divider = "border-l-2 border-[#CBD5E1]";

// Total-row cells. Two things live here rather than on the <tr>:
//   - the closing rule, because border-separate does not paint row borders
//   - the background, because tfoot is sticky and transparent cells would let
//     the statement scroll through the totals
const tfClass = "px-4 py-3 border-t-2 border-[#004BB5] bg-[#F8FAFC]";

// Same rule where it crosses the blue header — grey on blue would read as a
// seam, so it lifts to a tint of the header colour instead.
const headDivider = "border-l-2 border-[#4D82CC]";

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Safely get ledger group id from a voucher row.
// Receipt/Payment vouchers may store the FK as different column names —
// try all common variants so nothing silently maps to `undefined`.
// ─────────────────────────────────────────────────────────────────────────────
const getLedgerId = (row) =>
  row?.ledger_group_id ??
  row?.ledger_id ??
  row?.ledgerGroupId ??
  row?.ledger_group ??
  null;

// ─────────────────────────────────────────────────────────────────────────────
const BalanceSheet = () => {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [viewMode, setViewMode] = useState("simple"); // "simple" | "detailed"
  const [loading, setLoading] = useState(false);

  // Raw data
  const [accountGroups, setAccountGroups] = useState([]);
  const [ledgerGroups, setLedgerGroups] = useState([]);
  // Ledger entries replace the receipt/payment vouchers this page used to read.
  const [ledgerEntries, setLedgerEntries] = useState([]);
  /**
   * Financial years for this branch, and the one being viewed.
   *
   * Until this existed the page fetched every ledger row ever written and showed
   * an inception-to-date profit that never reset — year three read as the sum of
   * years one, two and three. Selecting a year sends fromDate/toDate, which
   * getModelData already understands for any model carrying a `date` column.
   *
   * `null` means "all time", kept as an option because it is the only view that
   * shows a closed year's result alongside the current one.
   */
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get("/api/financial-year", {
          baseURL: API_BASE_URL,
          params: branchId ? { branchId } : {},
        });
        const years = Array.isArray(res.data?.data) ? res.data.data : [];
        if (cancelled) return;
        setFinancialYears(years);
        // Default to the working year, so the page opens on the figures the
        // operator is actually working with rather than on all of history.
        const active = years.find((y) => y.is_active);
        setSelectedFY(active || null);
      } catch {
        if (!cancelled) { setFinancialYears([]); setSelectedFY(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [branchId]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFY]);

  const loadData = async () => {
    setLoading(true);
    try {
      const vf = JSON.stringify(
        isSuperAdmin ? {} : branchId ? { branchId } : {}
      );
      // Reads the LEDGER, not vouchers.
      //
      // This page previously summed receipt_voucher and payment_voucher, grouped
      // by account_group -> ledger_group. All four are the abandoned parallel
      // structure: account_group and ledger_group hold 0 rows, and the vouchers
      // never contained the opening balances or any ledger posting. The page
      // therefore rendered empty regardless of what the books held.
      //
      // base_ledger_group -> ledger_account -> ledger_transaction is the live
      // chain, and the one the day-end and the posting service both use.
      const [agRes, lgRes, ltRes] = await Promise.all([
        axios.get("/api/getModelData/base_ledger_group", {
          baseURL: API_BASE_URL,
          params: { filter: "{}" },
        }),
        axios.get("/api/getModelData/ledger_account", {
          baseURL: API_BASE_URL,
          params: { filter: "{}" },
        }),
        axios.get("/api/getModelData/ledger_transaction", {
          baseURL: API_BASE_URL,
          // Scoped to the selected financial year. Without the dates this read
          // the whole table for all time, so profit accumulated across years and
          // a closed year's result was counted again in the next one.
          params: {
            filter: vf,
            ...(selectedFY
              ? { fromDate: selectedFY.start_date, toDate: selectedFY.end_date }
              : {}),
          },
        }),
      ]);

      const toArr = (r) =>
        Array.isArray(r.data?.data)
          ? r.data.data
          : Array.isArray(r.data)
            ? r.data
            : [];

      // Map both tables into the shape the rest of this page already expects,
      // so only the data source changes and the accounting logic below is
      // untouched.
      //   base_ledger_group.group_name -> account_group
      //   base_ledger_group.group_type -> base_group   (uppercased: stored as "Asset")
      //   ledger_account.name          -> ledger_group
      //   ledger_account.ledger_id     -> account_group_id
      setAccountGroups(
        toArr(agRes).map((g) => ({
          id: g.id,
          account_group: g.group_name,
          base_group: String(g.group_type || "").trim().toUpperCase(),
        }))
      );
      setLedgerGroups(
        toArr(lgRes)
          .filter((a) => isSuperAdmin || !branchId || a.branchId == null || Number(a.branchId) === Number(branchId))
          .map((a) => ({
            id: a.id,
            ledger_group: a.name,
            account_group_id: a.ledger_id,
            branchId: a.branchId,
          }))
      );
      setLedgerEntries(toArr(ltRes));
    } catch (err) {
      console.error("Error loading balance sheet data:", err);
    }
    setLoading(false);
  };

  // ── Core accounting computation ───────────────────────────────────────────
  const {
    liabilities, assets, profitLoss, totalLiabilities, totalAssets,
    incomeRows, expenseRows,
  } = useMemo(() => {
    // ── Step 1: Sum receipts & payments per ledger_group id ──────────────
    // Rules for including a voucher row in balance sheet:
    //  1. ledger_type must be 'ledger' — member/staff rows store ledger_id
    //     as a ledger_account id (not ledger_group id), which can collide.
    //  2. payment_type / receipt_type must NOT be 'Loan Disbursement'.
    //  3. amount must be > 1 — all ₹1 entries (PV-4, PV-7, PV-14, etc.)
    //     are system-generated journal markers/placeholders, not real
    //     user-entered transactions. Real amounts are always > 1.
    // Sum ledger entries per account. `receipt` increases the account,
    // `payment` decreases it. Amounts are stored in PAISE (Ledger_section.js:148),
    // so they are divided by 100 here.
    const receiptsByLedger = {}; // { accountId: rupees in }
    const paymentsByLedger = {}; // { accountId: rupees out }
    const openingByLedger = {}; // { accountId: seeded opening balance }

    // ONE sign convention: `receipt` = debit, `payment` = credit — for real
    // postings and opening balances alike. Opening legs used to be written as
    // `receipt` on both sides and were special-cased here; they now sit on their
    // natural side (ledgerOpeningBalance.js, scripts/fix_opening_balance_sides.js),
    // so they are read like any other posting.
    ledgerEntries.forEach((e) => {
      const accountId = e.accountId;
      if (accountId == null) return;
      const amt = Number(e.amount || 0) / 100;
      if (!Number.isFinite(amt) || amt === 0) return;

      if (String(e.transaction_type).toLowerCase() === "receipt") {
        receiptsByLedger[accountId] = (receiptsByLedger[accountId] || 0) + amt;
      } else {
        paymentsByLedger[accountId] = (paymentsByLedger[accountId] || 0) + amt;
      }
    });

    // ── Step 2: Build account_group lookup ───────────────────────────────
    const agMap = {}; // { agId: accountGroup }
    accountGroups.forEach((ag) => {
      agMap[ag.id] = ag;
    });

    // ── Step 3: For each ledger_group, compute balance based on its
    //            account_group's base_group type.
    //
    //   ASSET    : balance = receipts(money in) - payments(money out)
    //              A positive number means we HAVE that much in the asset.
    //              Cash received increases it; cash paid out decreases it.
    //
    //   LIABILITY: balance = opening + payments(credit: borrowed) - receipts(debit: repaid)
    //   EQUITY   : same as LIABILITY — both sit on the credit side.
    //              Positive = we OWE that much.
    //
    //   INCOME   : balance = receipts(earned) - payments(refunds)
    //              Positive = we EARNED that much.
    //
    //   EXPENSE  : balance = payments(spent) - receipts(refunds received)
    //              Positive = we SPENT that much. (Sign is FLIPPED vs others)
    //
    // Liabilities read by SIDE, not by transaction_type. postPair writes the
    // credit leg as `payment`, so a Rs10,000 borrowing RECEIVED was being read
    // as Rs10,000 REPAID and Total Liabilities rendered as minus Rs10,000.
    // Opening balances are added separately because they use the other
    // convention (receipt on both legs) and already mean "we owe this".
    // ─────────────────────────────────────────────────────────────────────
    const getBaseGroup = (lg) => {
      const ag = agMap[lg.account_group_id];
      return ag?.base_group || "";
    };

    const balanceByLedger = {};
    ledgerGroups.forEach((lg) => {
      const rec = receiptsByLedger[lg.id] || 0;
      const pay = paymentsByLedger[lg.id] || 0;
      const opening = openingByLedger[lg.id] || 0;
      const bg = getBaseGroup(lg);

      if (bg === "EXPENSE") {
        // Expenses are DEBIT-side: postExpense writes Dr Operating Expenses as
        // `receipt`. Reading it as pay − rec turned Uchipuli's Rs.4,250 of
        // expenses into −4,250, which profit = income − expense then ADDED —
        // a Rs.8,500 gap between Liabilities and Assets.
        balanceByLedger[lg.id] = opening + rec - pay;
      } else if (bg === "LIABILITY" || bg === "EQUITY" || bg === "INCOME") {
        // Credit side: a credit leg (payment) means we owe MORE, or earned MORE.
        //
        // INCOME belongs HERE, not with ASSET. It is a credit balance, and every
        // income leg is written as `payment` — so reading it debit-side returned
        // 0 - 15,083 and rendered Rs15,083 of genuine interest and fees as a
        // Rs15,083 LOSS, knocking Total Liabilities to 30,949 against Total
        // Assets of 61,115.
        //
        // The liability half of this same fault was found and fixed earlier (see
        // the note above about a borrowing RECEIVED reading as REPAID); income
        // was left in the asset branch and stayed invisible because no income had
        // ever been posted. The ledger held exactly one disbursement pair until
        // EMI collection started crediting Interest Income.
        balanceByLedger[lg.id] = opening + pay - rec;
      } else {
        // ASSET — debit side: receipts increase the balance
        balanceByLedger[lg.id] = opening + rec - pay;
      }
    });

    // ── Step 4: Aggregate ledger_groups under their account_groups ────────
    const agBalances = {}; // { agId: { lgRows, agTotal } }
    accountGroups.forEach((ag) => {
      const lgRows = ledgerGroups
        .filter((lg) => lg.account_group_id === ag.id)
        .map((lg) => ({
          id: lg.id,
          name: lg.ledger_group,
          lgAmt: balanceByLedger[lg.id] || 0,
        }));
      const agTotal = lgRows.reduce((s, r) => s + r.lgAmt, 0);
      agBalances[ag.id] = { lgRows, agTotal };
    });

    // ── Step 5: Classify account_groups by side ───────────────────────────
    const ASSET_TYPES = ["ASSET"];
    const LIABILITY_TYPES = ["LIABILITY", "EQUITY"];
    const INCOME_TYPES = ["INCOME"];
    const EXPENSE_TYPES = ["EXPENSE"];

    const assetGroups = accountGroups.filter((ag) => ASSET_TYPES.includes(ag.base_group));
    const liabilityGroups = accountGroups.filter((ag) => LIABILITY_TYPES.includes(ag.base_group));
    const incomeGroups = accountGroups.filter((ag) => INCOME_TYPES.includes(ag.base_group));
    const expenseGroups = accountGroups.filter((ag) => EXPENSE_TYPES.includes(ag.base_group));

    // ── Step 6: P&L = Income − Expense (both are positive numbers now) ───
    const totalIncome = incomeGroups.reduce(
      (s, ag) => s + (agBalances[ag.id]?.agTotal || 0), 0
    );
    const totalExpense = expenseGroups.reduce(
      (s, ag) => s + (agBalances[ag.id]?.agTotal || 0), 0
    );
    const profitLoss = totalIncome - totalExpense; // + = profit, - = loss

    // ── Step 7: Build display rows ────────────────────────────────────────
    const buildRows = (groups) =>
      groups
        .map((ag) => ({
          agName: ag.account_group,
          agTotal: agBalances[ag.id]?.agTotal || 0,
          lgRows: agBalances[ag.id]?.lgRows || [],
        }))
        .filter((r) => r.agTotal !== 0 || r.lgRows.length > 0); // hide empty groups

    const liabilities = buildRows(liabilityGroups);
    const assets = buildRows(assetGroups);

    // What the P&L figure is MADE OF, for the detailed view.
    //
    // Income and expense were previously reduced to one number and thrown
    // away, which made "Profit And Loss" the only row on the sheet that could
    // not be expanded — the operator saw 4,000.00 with no way to learn it was
    // fee income plus loan interest.
    //
    // DISPLAY ONLY. These must never enter a total: `profitLoss` is already
    // added to totalLiabilities below, and counting the parts as well would
    // unbalance the sheet.
    const incomeRows = buildRows(incomeGroups);
    const expenseRows = buildRows(expenseGroups);

    // Total Liabilities side = all liability/equity groups + P&L
    const totalLiabilities =
      liabilities.reduce((s, r) => s + r.agTotal, 0) + profitLoss;

    // Total Assets side = all asset groups
    const totalAssets = assets.reduce((s, r) => s + r.agTotal, 0);

    return {
      liabilities, assets, profitLoss, totalLiabilities, totalAssets,
      incomeRows, expenseRows,
    };
  }, [accountGroups, ledgerGroups, ledgerEntries]);

  // ── Excel Export ──────────────────────────────────────────────────────────
  const exportExcel = () => {
    const rows = [["Liabilities", "Amount", "", "Assets", "Amount"]];
    const maxLen = Math.max(liabilities.length + 1, assets.length); // +1 for P&L row
    for (let i = 0; i < maxLen; i++) {
      const lib = liabilities[i];
      const ast = assets[i];
      rows.push([
        lib ? lib.agName : "",
        lib ? fmt(lib.agTotal) : "",
        "",
        ast ? ast.agName : "",
        ast ? fmt(ast.agTotal) : "",
      ]);
    }
    rows.push([`Profit And Loss (${profitLoss >= 0 ? "Profit" : "Loss"})`, fmt(Math.abs(profitLoss)), "", "", ""]);
    rows.push(["Total", fmt(totalLiabilities), "", "Total", fmt(totalAssets)]);
    const csvContent = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "balance_sheet.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Build flat list of display rows for each side
  const buildLibDisplayRows = () => {
    const rows = [];
    liabilities.forEach((ag) => {
      if (viewMode === "detailed") {
        ag.lgRows.forEach((lg) => {
          rows.push({ type: "lg", name: lg.name, lgAmt: lg.lgAmt, agAmt: null });
        });
        rows.push({ type: "ag", name: ag.agName, lgAmt: null, agAmt: ag.agTotal });
      } else {
        rows.push({ type: "ag-simple", name: ag.agName, amount: ag.agTotal });
      }
    });
    // P&L row.
    //
    // Labelled in BOTH directions. This used to append "(Loss)" when negative
    // and nothing when positive, so a profit rendered as a bare "Profit And
    // Loss" and the reader could not tell which it was. The Excel export above
    // already names both, so the screen was the one out of step.
    const plName = `Profit And Loss (${profitLoss >= 0 ? "Profit" : "Loss"})`;

    if (viewMode === "detailed") {
      // What the figure is made of, indented above it — the same `lg` row type
      // every other group already expands into, so no new styling.
      //
      // Expenses are NEGATED for display because profitLoss = income - expense:
      // printing a positive expense here would read as adding to the profit.
      incomeRows.forEach((ag) => {
        ag.lgRows.forEach((lg) => {
          rows.push({ type: "lg", name: lg.name, lgAmt: lg.lgAmt, agAmt: null });
        });
      });
      expenseRows.forEach((ag) => {
        ag.lgRows.forEach((lg) => {
          rows.push({ type: "lg", name: `${lg.name} (less)`, lgAmt: -lg.lgAmt, agAmt: null });
        });
      });
      rows.push({ type: "pl", name: plName, lgAmt: null, agAmt: profitLoss });
    } else {
      rows.push({ type: "pl-simple", name: plName, amount: profitLoss });
    }
    return rows;
  };

  const buildAstDisplayRows = () => {
    const rows = [];
    assets.forEach((ag) => {
      if (viewMode === "detailed") {
        ag.lgRows.forEach((lg) => {
          rows.push({ type: "lg", name: lg.name, lgAmt: lg.lgAmt, agAmt: null });
        });
        rows.push({ type: "ag", name: ag.agName, lgAmt: null, agAmt: ag.agTotal });
      } else {
        rows.push({ type: "ag-simple", name: ag.agName, amount: ag.agTotal });
      }
    });
    return rows;
  };

  const renderCombinedRows = () => {
    const libRows = buildLibDisplayRows();
    const astRows = buildAstDisplayRows();
    const maxLen = Math.max(libRows.length, astRows.length);

    // Amounts: one line always. A wrapped figure is unreadable, and they are
    // short enough that they never need to wrap.
    const tdClass = "px-4 py-2.5 text-sm text-[#1E293B] border-b border-[#F1F5F9] align-middle whitespace-nowrap";

    // Names: wrap rather than truncate. Detailed view puts eight columns through
    // a fixed layout, so a name column held ~14% of the width and every account
    // rendered as "Dindigul Borro…" — an elided name identifies nothing, which
    // defeats the point of the detailed view. align-top keeps a wrapped second
    // line sitting correctly beside its amount.
    const tdNameClass = "px-4 py-2.5 text-sm text-[#1E293B] border-b border-[#F1F5F9] align-top break-words";

    return Array.from({ length: maxLen }).map((_, i) => {
      const lib = libRows[i] || null;
      const ast = astRows[i] || null;
      const isPL = lib?.type === "pl" || lib?.type === "pl-simple";
      const isAG = lib?.type === "ag" || lib?.type === "ag-simple";
      const isAstAG = ast?.type === "ag" || ast?.type === "ag-simple";

      // Subtotal and P&L rows keep a fill because they ARE different in kind.
      // Ordinary rows lost their zebra striping: with a tinted header and the
      // centre divider doing the structural work, alternating fills only added
      // noise. Hover replaces it for tracking a row across to its amount.
      const rowBg = isPL
        ? "bg-[#FFFDF0]"
        : isAG || isAstAG
          ? "bg-[#F1F5F9]"
          : "bg-white hover:bg-[#F8FAFC]";

      if (viewMode === "simple") {
        return (
          <tr key={i} className={rowBg}>
            {/* Liabilities side */}
            <td className={`${tdNameClass} ${isPL || isAG ? `font-bold ${LIB_ACCENT}` : "text-[#475569]"}`}>
              {lib?.name || ""}
            </td>
            <td className={`${tdClass} text-right ${isPL || isAG ? `font-bold ${LIB_ACCENT}` : "font-semibold text-[#1E293B]"}`}>
              {lib ? fmt(lib.amount) : ""}
            </td>
            {/* Assets side */}
            <td className={`${tdNameClass} ${divider} ${isAstAG ? `font-bold ${AST_ACCENT}` : "text-[#475569]"}`}>
              {ast?.name || ""}
            </td>
            <td className={`${tdClass} text-right ${isAstAG ? `font-bold ${AST_ACCENT}` : "font-semibold text-[#1E293B]"}`}>
              {ast ? fmt(ast.amount) : ""}
            </td>
          </tr>
        );
      }

      // Detailed view — 4 cols each side
      const isPLDet = lib?.type === "pl";
      const isAGDet = lib?.type === "ag";
      const isLGDet = lib?.type === "lg";
      const isAstAGD = ast?.type === "ag";
      const isAstLGD = ast?.type === "lg";

      return (
        <tr key={i} className={rowBg}>
          {/* Liabilities — col 1: name (for AG/PL rows) */}
          <td className={`${tdNameClass} ${isPLDet || isAGDet ? `font-bold ${LIB_ACCENT}` : "text-[#475569]"}`}>
            {isLGDet ? "" : lib?.name || ""}
          </td>
          {/* col 2: lg name / amt by ledger group */}
          <td className={`${tdNameClass} text-[#475569]`}>
            {isLGDet ? lib?.name || "" : ""}
          </td>
          {/* col 3: account amt (lg amount) */}
          <td className={`${tdClass} text-right font-medium text-[#1E293B]`}>
            {isLGDet ? fmt(lib?.lgAmt ?? 0) : ""}
          </td>
          {/* col 4: amount by account group */}
          <td className={`${tdClass} text-right ${isPLDet || isAGDet ? `font-bold ${LIB_ACCENT}` : "text-[#475569]"}`}>
            {(isAGDet || isPLDet) ? fmt(lib?.agAmt ?? 0) : ""}
          </td>

          {/* Assets — col 1: name. Carries the centre divider. */}
          <td className={`${tdNameClass} ${divider} ${isAstAGD ? `font-bold ${AST_ACCENT}` : "text-[#475569]"}`}>
            {isAstLGD ? "" : ast?.name || ""}
          </td>
          {/* col 2: lg name */}
          <td className={`${tdNameClass} text-[#475569]`}>
            {isAstLGD ? ast?.name || "" : ""}
          </td>
          {/* col 3: account amt */}
          <td className={`${tdClass} text-right font-medium text-[#1E293B]`}>
            {isAstLGD ? fmt(ast?.lgAmt ?? 0) : ""}
          </td>
          {/* col 4: amount by account group */}
          <td className={`${tdClass} text-right ${isAstAGD ? `font-bold ${AST_ACCENT}` : "text-[#475569]"}`}>
            {isAstAGD ? fmt(ast?.agAmt ?? 0) : ""}
          </td>
        </tr>
      );
    });
  };

  return (
    <PageLayout>
      {/* Header */}
      <PageHeader
        title="Balance Sheet"
        // subtitle="Financial position — Assets vs Liabilities"
        // flex-nowrap, and every control given an explicit width. PageHeader's
        // own action wrapper is already `flex flex-wrap`; nesting a second
        // wrapping row inside it, with selects free to size themselves, is what
        // made these stack vertically down the right-hand side.
        actions={
          <div className="flex flex-nowrap items-center gap-2">
            {/* View Mode lives here rather than in a card of its own. A full-width
                panel holding one dropdown cost ~110px of height on a screen whose
                whole job is showing a table. */}
            <label className="text-sm font-medium text-[#475569] whitespace-nowrap">
              View
            </label>
            <select
              className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm bg-white w-[110px]"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
            >
              <option value="simple">Simple</option>
              <option value="detailed">Detailed</option>
            </select>

            {/* Which year the figures cover. "All time" is kept because it is the
                only view that shows a closed year's result next to the current
                one — every other option is deliberately one year. */}
            {financialYears.length > 0 && (
              <select
                className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm bg-white w-[165px]"
                value={selectedFY ? selectedFY.id : ""}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedFY(
                    id ? financialYears.find((y) => String(y.id) === id) || null : null
                  );
                }}
              >
                <option value="">All time</option>
                {financialYears.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.year_label}
                    {y.is_active ? " (current)" : y.closedAt ? " (closed)" : ""}
                  </option>
                ))}
              </select>
            )}
            {/* No Refresh button: loadData already runs on mount and whenever the
                selected year changes, so the figures cannot be stale without a
                user action. */}
            {/* <Button
              variant="secondary"
              onClick={exportExcel}
              startIcon={<i className="fas fa-file-excel mr-1.5" />}
            >
              Export Excel
            </Button> */}
          </div>
        }
      />

      {/* Balance mismatch warning */}
      {/* {!loading && Math.abs(totalLiabilities - totalAssets) > 0.01 && (
        <div className="flex items-center gap-2 p-4 mb-4 text-sm font-semibold text-warning-800 bg-warning-50 border border-warning-200 rounded-xl">
          <strong>Balance sheet does not balance.</strong> Assets (₹{fmt(totalAssets)}) ≠ Liabilities (₹{fmt(totalLiabilities)}). Check that all vouchers have the correct ledger group assigned.
        </div>
      )} */}

      {/* Balance Sheet Table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white border border-[#E2E8F0] rounded-xl shadow-sm flex-1 min-h-0">
          <Spinner size="lg" className="mb-3" />
          <p className="text-sm text-[#64748B]">Loading balance sheet data...</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm flex-1 min-h-0 flex flex-col">
          {/* Rows scroll inside the card so the total cards below stay on screen.
              Without this, four branches x ten groups pushed the totals past the
              fold with no way to reach them.

              Sized by flex, not by a pixel cap. PageLayout is already a
              fixed-height flex column (h-[calc(100vh-120px)]) whose content area
              is flex-1 min-h-0, so the card grows into whatever the header and
              stat cards leave and stays right at every window size — a
              hand-guessed max-h could only ever be correct at one height.

              min-h-0 is what permits the shrink: without it a flex item refuses
              to go below its content height and never scrolls.

              Printing keeps its natural height so every row appears on paper. */}
          <div className="overflow-auto flex-1 min-h-0 print:max-h-none print:overflow-visible">
            {/* border-separate, not border-collapse. With collapsed borders a
                sticky thead's own cell backgrounds are not reliably painted —
                the browser draws the collapsed border box instead — so the blue
                header rendered as white-on-transparent over the white card.
                Every rule in this table is an explicit border-b / border-l
                utility, nothing relies on collapsing, so the two are visually
                identical here. border-spacing-0 keeps the cells flush. */}
            <table className="w-full text-sm border-separate border-spacing-0 table-fixed">
              <colgroup>
                {/* Both name columns (1 and 2) carry account names, so the width
                    goes to them and the three amount columns take what they
                    actually need. Symmetrical across the two sides; sums to
                    100%. */}
                {viewMode === "detailed" ? (
                  <>
                    <col className="w-[16%]" />
                    <col className="w-[18%]" />
                    <col className="w-[8%]" />
                    <col className="w-[8%]" />
                    <col className="w-[16%]" />
                    <col className="w-[18%]" />
                    <col className="w-[8%]" />
                    <col className="w-[8%]" />
                  </>
                ) : (
                  <>
                    <col className="w-[30%]" />
                    <col className="w-[20%]" />
                    <col className="w-[30%]" />
                    <col className="w-[20%]" />
                  </>
                )}
              </colgroup>
              {/* sticky so the column meanings stay visible while rows scroll */}
              <thead className="sticky top-0 z-10 print:static">
                {/* The background sits on each th, not on the tr: thead is
                    sticky, and a sticky row with a transparent cell lets the
                    table body scroll through it. */}
                <tr className="text-[11px] font-semibold uppercase tracking-[0.06em]">
                  {viewMode === "detailed" ? (
                    <>
                      <th className={`${thBase} text-left`}>Liabilities</th>
                      <th className={`${thBase} text-center`}>Amount by Ledger Group</th>
                      <th className={`${thBase} text-right`}>Account Amt</th>
                      <th className={`${thBase} text-right`}>Amount by Account Group</th>
                      <th className={`${thBase} ${headDivider} text-left`}>Assets</th>
                      <th className={`${thBase} text-center`}>Amount by Ledger Group</th>
                      <th className={`${thBase} text-right`}>Account Amt</th>
                      <th className={`${thBase} text-right`}>Amount by Account Group</th>
                    </>
                  ) : (
                    <>
                      <th className={`${thBase} text-left`}>Liabilities</th>
                      <th className={`${thBase} text-right`}>Amount</th>
                      <th className={`${thBase} ${headDivider} text-left`}>Assets</th>
                      <th className={`${thBase} text-right`}>Amount</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>{renderCombinedRows()}</tbody>
              {/* Pinned to the bottom of the scroll container, as the header is
                  to the top. The totals are the line a reader most wants in
                  view, and on a long statement they scrolled away entirely.
                  print:static so paper gets normal flow instead of a repeated
                  or misplaced row. */}
              <tfoot className="sticky bottom-0 z-10 print:static">
                {/* The brand blue sits on the header, not here. A solid blue
                    band across the totals fought the header for attention and
                    read as a selected row rather than a summary; a firm top rule
                    closes the statement without shouting.

                    That rule is on the CELLS, not the tr. Under border-separate
                    a border set on a row is not painted at all — it only ever
                    showed because border-collapse merged it down into the
                    cells. */}
                <tr className="bg-[#F8FAFC] text-[#0F172A] font-bold text-sm">
                  {viewMode === "detailed" ? (
                    <>
                      <td className={`${tfClass} font-bold ${LIB_ACCENT}`}>Total</td>
                      <td className={tfClass} />
                      <td className={tfClass} />
                      <td className={`${tfClass} text-right font-bold ${LIB_ACCENT}`}>
                        {fmt(totalLiabilities)}
                      </td>
                      <td className={`${tfClass} font-bold ${divider} ${AST_ACCENT}`}>Total</td>
                      <td className={tfClass} />
                      <td className={tfClass} />
                      <td className={`${tfClass} text-right font-bold ${AST_ACCENT}`}>
                        {fmt(totalAssets)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className={`${tfClass} font-bold ${LIB_ACCENT}`}>Total</td>
                      <td className={`${tfClass} text-right font-bold ${LIB_ACCENT}`}>
                        {fmt(totalLiabilities)}
                      </td>
                      <td className={`${tfClass} font-bold ${divider} ${AST_ACCENT}`}>Total</td>
                      <td className={`${tfClass} text-right font-bold ${AST_ACCENT}`}>
                        {fmt(totalAssets)}
                      </td>
                    </>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* No summary cards. Total Assets, Total Liabilities and Net Profit all
          repeat what the table's own Total and Profit-and-Loss rows already
          show, and cost a band of height the statement can use instead.

          The fourth card, "Lending Not Yet Funded", went with them. That
          condition still stops work where it matters: Day-End v2 blocks the
          close on it and names the receipt to record. */}
    </PageLayout>
  );
};

export default BalanceSheet;